/**
 * Reunification WebSocket handlers for Aegix One.
 *
 * Ported from AIM (`apps/aegix-aim-react/src/contexts/socket-handlers/
 * reunificationHandlers.ts`), adapted to ONE's architecture:
 *  - `@tanstack/react-query` v5 query keys (invalidateQueries({ queryKey }))
 *  - Guardian-facing: no overlay push, no Redux, no admin flows
 *  - Notifications surface via `['notifications']` / `['unread-count']` keys
 *
 * Registered against all reunification.* event types listed below.
 * Events continue to flow after alert end, so these handlers stay
 * subscribed for the full authenticated session.
 *
 * Event taxonomy:
 *   reunification.event.{activated,paused,resumed,completed}
 *   reunification.alert.ended
 *   reunification.student.{status_changed,released}
 *   reunification.guardian.{arrived,verified,matched}
 *   reunification.staff.{assigned,checked_in,reassigned,released}
 *   reunification.stats.updated
 */

import type { QueryClient } from '@tanstack/react-query';
import type { HandlerDeps, SocketHandlerMap } from './types';

interface ReunificationEventPayload {
  event_id?: string;
  [key: string]: unknown;
}

interface ReunificationStudentPayload extends ReunificationEventPayload {
  student_id?: string;
}

interface ReunificationGuardianPayload extends ReunificationEventPayload {
  guardian_id?: string;
}

/**
 * Invalidate the React Query keys that back any view of reunification
 * events. Scoped by event id when present; otherwise broad.
 */
function invalidateEventQueries(
  queryClient: QueryClient,
  eventId: string | undefined,
) {
  queryClient.invalidateQueries({ queryKey: ['events'] });
  if (eventId) {
    queryClient.invalidateQueries({ queryKey: ['event', eventId] });
  } else {
    queryClient.invalidateQueries({ queryKey: ['event'] });
  }
}

function invalidateStudentQueries(
  queryClient: QueryClient,
  eventId: string | undefined,
) {
  if (eventId) {
    queryClient.invalidateQueries({ queryKey: ['event-students', eventId] });
  } else {
    queryClient.invalidateQueries({ queryKey: ['event-students'] });
  }
  queryClient.invalidateQueries({ queryKey: ['my-students'] });
}

function invalidateGuardianQueries(
  queryClient: QueryClient,
  eventId: string | undefined,
) {
  if (eventId) {
    queryClient.invalidateQueries({ queryKey: ['guardian-self', eventId] });
  } else {
    queryClient.invalidateQueries({ queryKey: ['guardian-self'] });
  }
}

function refreshNotifications(queryClient: QueryClient) {
  queryClient.invalidateQueries({ queryKey: ['notifications'] });
  queryClient.invalidateQueries({ queryKey: ['unread-count'] });
}

export function createReunificationHandlers(deps: HandlerDeps): SocketHandlerMap {
  const { queryClient } = deps;

  const handleEventLifecycle = (data: unknown) => {
    const payload = (data ?? {}) as ReunificationEventPayload;
    invalidateEventQueries(queryClient, payload.event_id);
    refreshNotifications(queryClient);
  };

  const handleStudentUpdate = (data: unknown) => {
    const payload = (data ?? {}) as ReunificationStudentPayload;
    invalidateStudentQueries(queryClient, payload.event_id);
    invalidateEventQueries(queryClient, payload.event_id);
    refreshNotifications(queryClient);
  };

  const handleGuardianUpdate = (data: unknown) => {
    const payload = (data ?? {}) as ReunificationGuardianPayload;
    invalidateGuardianQueries(queryClient, payload.event_id);
    invalidateEventQueries(queryClient, payload.event_id);
    refreshNotifications(queryClient);
  };

  const handleStaffUpdate = (data: unknown) => {
    const payload = (data ?? {}) as ReunificationEventPayload;
    invalidateEventQueries(queryClient, payload.event_id);
    refreshNotifications(queryClient);
  };

  const handleStatsUpdate = (data: unknown) => {
    const payload = (data ?? {}) as ReunificationEventPayload;
    invalidateEventQueries(queryClient, payload.event_id);
  };

  return {
    // Event lifecycle
    'reunification.event.activated': handleEventLifecycle,
    'reunification.event.paused': handleEventLifecycle,
    'reunification.event.resumed': handleEventLifecycle,
    'reunification.event.completed': handleEventLifecycle,

    // Parent alert ended — reunification stays ACTIVE/PAUSED (ADR-106 §4).
    // Payload carries alert_history_id; side-effects match lifecycle events
    // so UI chips/banners driven by event queries flip without a refresh.
    'reunification.alert.ended': handleEventLifecycle,

    // Student state
    'reunification.student.status_changed': handleStudentUpdate,
    'reunification.student.released': handleStudentUpdate,

    // Guardian state
    'reunification.guardian.arrived': handleGuardianUpdate,
    'reunification.guardian.verified': handleGuardianUpdate,
    'reunification.guardian.matched': handleGuardianUpdate,

    // Staff state
    'reunification.staff.assigned': handleStaffUpdate,
    'reunification.staff.checked_in': handleStaffUpdate,
    'reunification.staff.reassigned': handleStaffUpdate,
    'reunification.staff.released': handleStaffUpdate,

    // Aggregate stats
    'reunification.stats.updated': handleStatsUpdate,
  };
}
